import { defineStore } from 'pinia'

import { StoreId } from '@/store'

import { Success } from '@/api/results'
import type { Address, AddressNote } from '@/types/api/api-v3/addresses'
import type { LoadState } from '@/types/main'
import type { AddressesState } from '@/types/store/addresses'

const defaultState: AddressesState = {
  addresses: {
    isLoaded: false,
    isLoading: false,
    data: [],
    error: null,
  },
}

export const useAddressesStore = defineStore(StoreId.ADDRESSES, {
  state: (): AddressesState => defaultState,
  actions: {
    async getAddresses() {
      this.setAddresses({ isLoading: true })

      const { $apiService } = useNuxtApp()
      const result = await $apiService.addresses.fetchNewAddresses()

      if (result instanceof Success) {
        this.setAddresses({ data: result.data, error: null })
      } else {
        this.setAddresses({ error: result })
      }

      this.setAddresses({ isLoading: false, isLoaded: true })

      return result
    },

    setAddresses(addresses: Partial<LoadState<Address[]>>) {
      Object.assign(this.$state.addresses, addresses)
    },

    async createAddresses(bodyRequest: {
      street: string
      house: string
      apartment: string
      porch: string
      floor: string
      intercom: string
      notes: string
    }) {
      const { $apiService } = useNuxtApp()
      const result = await $apiService.addresses.createAddresses(bodyRequest)

      if (result instanceof Success) {
        return result.data
      }
    },

    async getSuggestions(bodyRequest: {
      provider: string | undefined
      state: string | undefined
      city: string
      alias: string
      search_term: string | null
      longitude: object | undefined
      latitude: object | undefined
    }) {
      const { $apiService } = useNuxtApp()
      const result = await $apiService.addresses.getSuggestions(bodyRequest)

      if (result instanceof Success) {
        return result.data
      }
    },

    async updateAddresses(newAddress: Address) {
      const { $apiService } = useNuxtApp()
      return await $apiService.addresses.updateAddresses(newAddress)
    },

    async updateAddressNote(newAddressNote: AddressNote) {
      const { $apiService } = useNuxtApp()

      return await $apiService.addresses.updateAddressNote(newAddressNote)
    },

    async deleteAddresses(addressId: number) {
      const { $apiService } = useNuxtApp()
      return await $apiService.addresses.deleteAddress(addressId)
    },
  },
  getters: {},
})
